import { useEffect, useRef, useContext } from "react";
import * as THREE from "three";
import { ThemeContext } from "../App";

const BackgroundEffect = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    if (!containerRef.current) return;

    // Store reference to the current DOM element
    const currentContainer = containerRef.current;

    // Setup scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 10;
    camera.position.y = 1;
    camera.position.x = -1;
    camera.lookAt(scene.position) 
    camera.fov = 70; // Wider field of view
    camera.updateProjectionMatrix();

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    currentContainer.appendChild(renderer.domElement);

    // Color schemes based on theme
    const colors =
      theme === "light"
        ? {
            sky: ["#87CEEB", "#E0F7FA"], // Light blue sky gradient
            mountains: ["#4B6455", "#7D9D9C", "#A1C7C7"], // Green mountain layers
            ground: "#8FBC8F", // Light green ground
            celestial: "#FDB813", // Sun color
          }
        : {
            sky: ["#0F1729", "#2C3E50"], // Dark blue night sky
            mountains: ["#1a2639", "#273746", "#34495E"], // Dark blue mountain layers
            ground: "#1D3E53", // Dark blue-green ground
            celestial: "#E1E1E1", // Moon color
          };

    // Create sky background
    const skyGeometry = new THREE.PlaneGeometry(80, 30);
    const skyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        colorTop: { value: new THREE.Color(colors.sky[0]) },
        colorBottom: { value: new THREE.Color(colors.sky[1]) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 colorTop;
        uniform vec3 colorBottom;
        varying vec2 vUv;
        
        void main() {
          vec3 color = mix(colorBottom, colorTop, vUv.y);
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });

    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    sky.position.z = -15;
    scene.add(sky);

    // Create sun/moon
    const createCelestialBody = () => {
      const celestialGeometry = new THREE.CircleGeometry(1.5, 32);
      const celestialMaterial = new THREE.MeshBasicMaterial({
        color: colors.celestial,
        transparent: true,
        opacity: theme === "light" ? 0.9 : 0.8,
      });
      const celestialBody = new THREE.Mesh(
        celestialGeometry,
        celestialMaterial
      );

      // Position in upper corner
      celestialBody.position.set(
        theme === "light" ? 10 : -10, // Sun on right, moon on left
        6,
        -12
      );

      scene.add(celestialBody);
      return celestialBody;
    };

    const celestialBody = createCelestialBody();

    // Create simplified mountain ranges
    const createMountainRange = (
      z: number,
      color: string,
      count: number,
      height: number,
      width: number = 40 // Default width if not provided
    ) => {
      const shape = new THREE.Shape();
    
      // Start at the left edge, below the viewport
      shape.moveTo(-width / 2, -10);
    
      // Create random mountain peaks
      for (let i = 0; i < count; i++) {
        const x = -width / 2 + (width * i) / (count - 1);
        const y = Math.random() * height;
        shape.lineTo(x, y);
      }
    
      // Complete the shape by going to the right edge and then down
      shape.lineTo(width / 2, -10);
    
      const geometry = new THREE.ShapeGeometry(shape);
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
      });
      const mountains = new THREE.Mesh(geometry, material);
      mountains.position.z = z;
      scene.add(mountains);
    };

    // Create three layers of mountains at different distances
    createMountainRange(-14, colors.mountains[0], 12, 3, 80); // Far mountains (wider)
    createMountainRange(-12, colors.mountains[1], 8, 5, 80); // Mid mountains (wider)
    createMountainRange(-10, colors.mountains[2], 8, 4, 80); 

    // Create ground plane
    const groundGeometry = new THREE.PlaneGeometry(60, 15);
    const groundMaterial = new THREE.MeshBasicMaterial({
      color: colors.ground,
      transparent: true,
      opacity: 0.7,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.position.y = -7;
    ground.position.z = -8;
    scene.add(ground);

    // Add stars if in dark mode
    if (theme === "dark") {
      const starsGeometry = new THREE.BufferGeometry();
      const starsCount = 200;
      const starsPositions = new Float32Array(starsCount * 3);
      const starsSizes = new Float32Array(starsCount);

      for (let i = 0; i < starsCount; i++) {
        const i3 = i * 3;
        starsPositions[i3] = (Math.random() - 0.5) * 60;
        starsPositions[i3 + 1] = Math.random() * 20;
        starsPositions[i3 + 2] = -14 + Math.random() * 4;

        // Varied star sizes
        starsSizes[i] = Math.random() * 0.05 + 0.01;
      }

      starsGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(starsPositions, 3)
      );
      starsGeometry.setAttribute(
        "size",
        new THREE.BufferAttribute(starsSizes, 1)
      );

      // Create star texture
      const starCanvas = document.createElement("canvas");
      starCanvas.width = 16;
      starCanvas.height = 16;
      const starCtx = starCanvas.getContext("2d");
      if (starCtx) {
        const gradient = starCtx.createRadialGradient(8, 8, 0, 8, 8, 8);
        gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
        gradient.addColorStop(0.5, "rgba(240, 240, 255, 0.5)");
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        starCtx.fillStyle = gradient;
        starCtx.fillRect(0, 0, 16, 16);
      }

      const starTexture = new THREE.CanvasTexture(starCanvas);

      const starsMaterial = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.1,
        map: starTexture,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });

      const stars = new THREE.Points(starsGeometry, starsMaterial);
      scene.add(stars);
    }

    // Animation
    const clock = new THREE.Clock();

    const animate = () => {
      const animationId = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();

      // Very gentle camera movement
      camera.position.x = Math.sin(time * 0.1) * 0.2;
      camera.position.y = 1 + Math.cos(time * 0.1) * 0.1;
      camera.lookAt(0, 0, -10);

      // Subtle sun/moon pulsing
      if (celestialBody) {
        const pulseAmount = Math.sin(time * 0.5) * 0.03 + 1;
        celestialBody.scale.set(pulseAmount, pulseAmount, 1);
      }

      renderer.render(scene, camera);
      return animationId;
    };

    const animationId = animate();

    // Handle window resize
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", handleResize);

      if (currentContainer && currentContainer.contains(renderer.domElement)) {
        currentContainer.removeChild(renderer.domElement);
      }

      // Dispose resources
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Points) {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });

      renderer.dispose();
    };
  }, [theme]); // Re-render when theme changes

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        opacity: 0.6, // Consistent opacity
        pointerEvents: "none",
      }}
    />
  );
}

export default BackgroundEffect;
